import { chain, IterateeShorthand } from 'lodash'
import Vue from 'vue'
import { Module } from 'vuex'

import { apolloClient } from '@/apollo-provider'

import {
  //AllMessagesQuery,
  AllMessagesQueryVariables,
  CaseMessageNodeFragment,
  CaseNodeFragment,
  DeleteCaseMessageMutation,
  UpdateCaseMessageMutation,
  UpdateCaseMessageMutationVariables,
} from '@/graphql-schema'

import {
  //AllMessages,
  AllMessagesInCase,
  AllMessagesInCaseAfter,
  CreateCaseMessage,
  DeleteCaseMessage,
  UpdateCaseMessage,
  CaseMessageNode,
} from '@/queries/caseMessages.graphql'
import { UpdateNewMessagesAfter, UpdateFavoriteMessage } from '@/queries/involvements.graphql'

export interface ModuleState {
  caseMessages: { [key: string]: CaseMessageNodeFragment }
}

interface InCaseTypeInvolvement {
  inCase: Pick<CaseNodeFragment, 'myInvolvement'>
}

interface InCaseTypeId {
  inCase: Pick<CaseNodeFragment, 'id'>
}

interface MessageTypeId {
  message: Pick<CaseMessageNodeFragment, 'id'>
}

interface MessageFragmentType {
  message: Pick<
    CaseMessageNodeFragment,
    | 'id'
    | 'case'
    | 'body'
    | 'sentAt'
    | 'toInterviewers'
    | 'toPatients'
    | 'toSupervisors'
    | 'toSupporters'
    | 'toTherapists'
  >
}

interface MessageComponentFragmentType {
  messages: CaseMessageNodeFragment[]
}

const moduleCaseMessages: Module<ModuleState, any> = {
  actions: {
    USER_LOGGED_OUT: {
      root: true,
      handler({ commit }) {
        commit({ type: 'resetState' })
      },
    },
    /*USER_SIGNED_IN: {
      root: true,
      handler({ dispatch }) {
        return dispatch({ type: 'query' })
      },
    },*/
    //CS 07.12.22: it seems that those messages are nowhere used
    //due to new message system, where messages are taken
    // via postgrest-interface (and only relevant data)
    //allMessages seem not to be used any longer
    //if caseMessages will be needed, they are fetched directly inside the
    //corresponding view
    /*query({ commit }) {
      return apolloClient
        .query<AllMessagesQuery, AllMessagesQueryVariables>({
          fetchPolicy: 'no-cache',
          query: AllMessages,
        })
        .then(query => {
          commit({ type: 'setMessages', messages: query.data.caseMessages!.nodes || [] })
        })
    },*/
    updateTimestamp({ dispatch, rootGetters }, { inCase }: InCaseTypeInvolvement) {
      if (!inCase.myInvolvement) {
        if (rootGetters.currentUser.is_coordinator_account) {
          return
        } else {
          throw new Error('not involved in this case!')
        }
      }
      const now = new Date()
      return apolloClient
        .mutate({
          mutation: UpdateNewMessagesAfter,
          variables: {
            id: inCase.myInvolvement.id,
            timestamp: now,
          },
        })
        .then(() => {
          return Promise.all([
            //dispatch({ type: 'queryCases' }, { root: true }),
          ])
        })
    },
    updateFavoriteMessage({ dispatch, rootGetters }, { inCase }: InCaseTypeInvolvement) {
      return apolloClient
        .mutate({
          mutation: UpdateFavoriteMessage,
          variables: {
            id: inCase.myInvolvement.id,
            isFavorite: inCase.myInvolvement.favoriteMessage,
          },
        })
        .then(() => {
          return Promise.all([
            dispatch({ type: 'query' }),
            dispatch({ type: 'queryCases' }, { root: true }),
          ])
        })
    },
    addMessage({ dispatch, rootGetters }, { inCase }: InCaseTypeId) {
      return apolloClient
        .mutate({
          mutation: CreateCaseMessage,
          variables: {
            message: {
              caseId: inCase.id,
              senderId: rootGetters.currentUserId,
            },
          },
        })
        .then(() => dispatch({ type: 'updateTimestamp', inCase }))
    },
    dropMessage({ dispatch }, { message }: MessageTypeId) {
      console.log('drop message')
      return apolloClient
        .mutate<DeleteCaseMessageMutation>({
          mutation: DeleteCaseMessage,
          variables: { id: message.id },
        })
        .then(() => {
          return dispatch({ type: 'query' })
        })
    },
    updateMessage(
      { dispatch },
      { message }: MessageFragmentType
    ): Promise<CaseMessageNodeFragment | undefined | null> {
      if (!message || !message.id) {
        throw new Error('provide a message with an id')
      }

      const variables: UpdateCaseMessageMutationVariables = {
        id: message.id,
        patch: {
          body: message.body,
          sentAt: message.sentAt,
          toInterviewers: message.toInterviewers,
          toPatients: message.toPatients,
          toSupervisors: message.toSupervisors,
          toSupporters: message.toSupporters,
          toTherapists: message.toTherapists,
        },
      }

      return apolloClient
        .mutate<UpdateCaseMessageMutation>({
          mutation: UpdateCaseMessage,
          variables,
        })
        .then(({ data }) => {
          return data!.updateCaseMessageById!.caseMessage
        })
        .then(updatedMessage => {
          return dispatch({ type: 'updateTimestamp', inCase: updatedMessage!.case }).then(
            () => updatedMessage
          )
        })
        .then(updatedMessage => {
          return updatedMessage
        })
    },
  },
  getters: {
    inCase: state => ({ inCase, orderBy = 'sentAt', desc = false }): CaseMessageNodeFragment[] => {
      if (!inCase) {
        return []
      }
      return chain(state.caseMessages)
        .filter(m => m.caseId === inCase.id)
        .orderBy(orderBy, desc ? 'desc' : 'asc')
        .value()
    },
    // CS 19.07.2022: Was will mir diese Funktion hier sagen??? Ich verstehe das ganze Konstrukt überhaupt nicht - irgendwie sehr wirr!!
    /*orderedBySentAtDesc: state =>
      chain(state.caseMessages)
        .orderBy('sentAt', 'desc')
        .value(),
    query: state => ({
      properties,
      orderBy = 'sentAt',
      desc = false,
    }: {
      properties: IterateeShorthand<CaseMessageNodeFragment>
      orderBy: keyof CaseMessageNodeFragment
      desc: boolean
    }): CaseMessageNodeFragment[] => {
      return chain(state.caseMessages)
        .filter(properties || (m => true))
        .orderBy(orderBy, desc ? 'desc' : 'asc')
        .value()
    },*/
    unread: (state, getters, rootState, rootGetters): CaseMessageNodeFragment[] => {
      return chain(state.caseMessages)
        .filter(m => !!m.isUnread && m.senderId !== rootGetters.currentUserId)
        .value()
    },
  },
  mutations: {
    setMessages(state, { messages }: MessageComponentFragmentType) {
      //setting caseMessages to null is necessary to avoid memory leak of vuex
      state.caseMessages = null
      //setting caseMessages to an empty object will allow the garbage collector to clean up
      state.caseMessages = {}
      state.caseMessages = chain(messages)
        .keyBy('id')
        .value()
      //cleanup of temporary message-Array will spare memory
      messages.splice(0, messages.length)
      //it's unclear why messages are returned - since it's not used in calling hierarchy
      //so it's commented out here
      //return messages
    },
    resetState(state) {
      state.caseMessages = {}
    },
  },
  state: {
    caseMessages: {},
  },
}

export default moduleCaseMessages
